import React, { useState, useCallback, useEffect, useMemo } from "react";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import SearchIcon from "../../../assets/svg/search-icon";

import { SearchableDropdownOption } from "../../SearchableDropdown/types";
import {
  selectCommunicationsFilters,
  selectCrmLeadsFilters,
  setCommunicationsTags,
  setCrmLeadsTags,
} from "../../../redux/slices/filterSlice";
import { useDispatch, useSelector } from "react-redux";
import { useGetLeadDetailTagsQuery } from "../../../redux/services/communicationApi";
import { RootState } from "../../../redux/store";
import { getLeadTagDisplayName } from "../../../utils/constants/mappings";

interface Tag {
  code: string;
  name: string;
}

const TagsFilter = ({
  onClose,
  isCommunication = false,
}: {
  onClose: () => void;
  isCommunication?: boolean;
}) => {
  const crmLeadsFilters = useSelector(selectCrmLeadsFilters);
  const communicationsFilters = useSelector(selectCommunicationsFilters);
  const clientCode = useSelector(
    (state: RootState) => state?.auth?.client?.client_code
  );
  const selectedTags = isCommunication
    ? communicationsFilters?.tags
    : crmLeadsFilters?.tags;

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredTags, setFilteredTags] = useState<SearchableDropdownOption[]>(
    []
  );
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(
    selectedTags.value as string[]
  );
  const dispatch = useDispatch();

  const { data: tags, isLoading, isFetching } = useGetLeadDetailTagsQuery({});

  // Transform tags data to dropdown options
  const transformedTags = useMemo(
    () =>
      tags?.map((tag: Tag) => ({
        value: tag.code,
        label: getLeadTagDisplayName(tag.name, clientCode),
      })) || [],
    [tags, clientCode]
  );

  // Handle search functionality
  const handleSearch = useCallback(
    (query: string) => {
      if (!query.trim()) {
        setFilteredTags(transformedTags);
        return;
      }

      const filtered = transformedTags.filter(
        (user: SearchableDropdownOption) =>
          user.label?.toString().toLowerCase().includes(query.toLowerCase())
      );
      setFilteredTags(filtered);
    },
    [transformedTags]
  );

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  // Initialize filtered users
  useEffect(() => {
    setFilteredTags(transformedTags);
  }, [transformedTags]);

  const handleUserSelect = (userId: string) => {
    setSelectedTagIds((prevIds) => {
      if (prevIds.includes(userId)) {
        return prevIds.filter((id) => id !== userId);
      } else {
        return [...prevIds, userId];
      }
    });
  };

  const handleApply = () => {
    const selectedDisplayValues = selectedTagIds
      .map(
        (id) =>
          transformedTags.find((u: SearchableDropdownOption) => u.value === id)
            ?.label
      )
      .filter(Boolean) as string[];

    dispatch(
      isCommunication
        ? setCommunicationsTags({
            value: selectedTagIds,
            displayValue: selectedDisplayValues,
          })
        : setCrmLeadsTags({
            value: selectedTagIds,
            displayValue: selectedDisplayValues,
          })
    );
    onClose();
  };

  const isApplyDisabled = () => {
    return (
      JSON.stringify([...(selectedTagIds || [])].sort()) ===
      JSON.stringify([...((selectedTags?.value as string[]) || [])].sort())
    );
  };
  return (
    <div className="flex flex-col h-[92vh] overflow-hidden">
      {/* Search Input */}
      <div className="relative border-b border-border-gray dark:border-border-gray-dk">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search Tags"
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>

      {/* Selected Users Display - Independent scroll */}
      {selectedTagIds.length > 0 && (
        <div className="flex-shrink-0">
          <span className="text-xxs font-semibold text-data dark:text-data-dk rounded-sm px-3 py-1 block">
            SELECTED ({selectedTagIds?.length ?? 0})
          </span>
          <div className="max-h-[200px] overflow-y-auto">
            <div className="flex flex-col divide-y divide-border-gray dark:divide-border-gray-dk">
              {selectedTagIds.map((tagId) => (
                <div
                  key={tagId}
                  onClick={() => handleUserSelect(tagId)}
                  className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                      {
                        transformedTags.find(
                          (u: SearchableDropdownOption) => u.value === tagId
                        )?.label
                      }
                    </span>
                  </div>
                  <div className="flex items-center justify-center w-3 h-3 border rounded border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk">
                    <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Users List - Takes remaining space */}
      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex-shrink-0 px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
          All Tags
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading || isFetching ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {isLoading ? "Loading tags..." : "Fetching tags..."}
              </div>
            </div>
          ) : filteredTags.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {searchQuery.trim() ? "No tags found" : "No tags available"}
              </div>
            </div>
          ) : (
            filteredTags
              ?.filter(
                (tag: SearchableDropdownOption) =>
                  !selectedTagIds.includes(tag.value)
              )
              ?.map((tag: SearchableDropdownOption) => (
                <div
                  key={tag.value}
                  onClick={() => handleUserSelect(tag.value)}
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                      {tag.label}
                    </div>
                  </div>
                  <div className="flex items-center justify-center w-3 h-3 border rounded border-border-gray dark:border-border-gray-dk">
                    <div className="w-3 h-3 rounded-sm bg-transparent" />
                  </div>
                </div>
              ))
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-4 px-3 pb-2">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={onClose}
        />
        <Button
          innerText="Apply"
          disabled={isApplyDisabled()}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default TagsFilter;
